import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  Check, Video, FileText, Wrench, MessageCircle, Users, Phone, 
  Package, ShieldCheck, Globe, Crown, Sparkles,
  ArrowRight, Lock, Timer, Bot, BarChart3, Target, Zap, Rocket, X, Clock,
  Calendar, Play, Headphones, UserCheck, Info, CreditCard, Search, CheckCircle, ZoomIn, Gift,
  ChevronDown, Map, Briefcase, TrendingUp, Settings, ShoppingCart
} from 'lucide-react';
import CheckoutModal from './CheckoutModal';
import { usePrices } from '../../context/PricesContext';
import { usePromo } from '../../context/PromoContext';

// Store scroll position globally
let savedScrollPosition = 0;

// Utility functions for body scroll lock - CSS approach that doesn't change document flow
const lockBodyScroll = () => {
  if (!document.body.classList.contains('modal-open')) {
    savedScrollPosition = window.scrollY || window.pageYOffset;
    document.body.classList.add('modal-open');
    // Use touch-action to prevent mobile scroll and overflow to prevent desktop scroll
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  }
};

const unlockBodyScroll = () => {
  const scrollY = savedScrollPosition;
  document.body.classList.remove('modal-open');
  document.body.style.overflow = '';
  document.body.style.touchAction = '';
  // Immediately restore scroll position
  window.scrollTo(0, scrollY);
};

// Hook - just locks on open, unlock is handled manually in handleClose
const useBodyScrollLock = (isOpen) => {
  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    }
    // No cleanup - unlocking is done manually in handleClose
  }, [isOpen]);
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Image Lightbox Component - для увеличения скриншотов (rendered via portal)
const ImageLightbox = ({ src, alt, isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm cursor-zoom-out"
      onClick={onClose}
    >
      <button 
        onClick={onClose}
        className="absolute top-4 right-4 w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors z-10"
      >
        <X className="w-6 h-6" />
      </button>
      <img 
        src={src} 
        alt={alt} 
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
};

// Clickable Image with Zoom
const ZoomableImage = ({ src, alt, className = "" }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  
  const handleClick = (e) => {
    e.stopPropagation();
    setIsZoomed(true);
  };
  
  return (
    <>
      <div 
        className={`relative cursor-zoom-in group ${className}`}
        onClick={handleClick}
      >
        <img src={src} alt={alt} className="w-full h-auto rounded-lg" />
        {/* Zoom hint - always visible on mobile, hover on desktop */}
        <div className="absolute inset-0 bg-black/0 sm:group-hover:bg-black/30 transition-colors rounded-lg flex items-center justify-center pointer-events-none">
          <div className="absolute bottom-2 right-2 sm:static w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-white/90 flex items-center justify-center sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-lg">
            <ZoomIn className="w-4 h-4 sm:w-6 sm:h-6 text-zinc-800" />
          </div>
        </div>
      </div>
      <ImageLightbox src={src} alt={alt} isOpen={isZoomed} onClose={() => setIsZoomed(false)} />
    </>
  );
};

// Бонусные продукты - конфиг (цены загружаются динамически)
const bonusProductsConfig = [
  { id: 'ungating', priceKey: 'ungating', name: 'Ангейтинг', desc: 'Открытие закрытых брендов', icon: 'unlock', color: 'amber' },
  { id: 'supplier-invoice', priceKey: 'supplier_invoice', name: 'Поставщик и инвойс', desc: 'Критерии проверки Amazon', icon: 'file', color: 'blue' },
  { id: 'sourcing-pack', priceKey: 'sourcing_pack', name: 'Sourcing Pack', desc: 'Поиск товаров и поставщиков', icon: 'search', color: 'emerald' },
  { id: 'asin-checker', priceKey: 'asin_checker', name: 'ASIN Checker', desc: 'Проверка открытых/закрытых товаров', icon: 'bot', color: 'cyan' },
  { id: 'repricer', priceKey: 'repricer', name: 'Репрайсер', desc: 'Настройка под прибыль', icon: 'chart', color: 'violet' },
];

// Компонент бонусных продуктов для модальных окон
const BonusProductsSection = ({ onClose, accentColor = 'amber' }) => {
  // Get prices from context
  const { getPrice } = usePrices();
  
  // Build bonusProducts with current prices
  const bonusProducts = bonusProductsConfig.map(p => ({
    ...p,
    price: getPrice(p.priceKey)
  }));
  const totalBonusValue = bonusProducts.reduce((sum, p) => sum + (p.price || 0), 0);

  const colorClasses = {
    amber: { border: 'border-amber-500/30', bg: 'from-amber-500/10 to-orange-500/10', text: 'text-amber-400', badge: 'bg-amber-500/20 border-amber-500/40 text-amber-400' },
    blue: { border: 'border-blue-500/30', bg: 'from-blue-500/10 to-indigo-500/10', text: 'text-blue-400', badge: 'bg-blue-500/20 border-blue-500/40 text-blue-400' },
    violet: { border: 'border-violet-500/30', bg: 'from-violet-500/10 to-purple-500/10', text: 'text-violet-400', badge: 'bg-violet-500/20 border-violet-500/40 text-violet-400' },
    emerald: { border: 'border-emerald-500/30', bg: 'from-emerald-500/10 to-teal-500/10', text: 'text-emerald-400', badge: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' },
  };
  const colors = colorClasses[accentColor];
  
  const iconMap = {
    unlock: <Lock className="w-4 h-4" />,
    file: <FileText className="w-4 h-4" />,
    search: <Search className="w-4 h-4" />,
    bot: <Bot className="w-4 h-4" />,
    chart: <BarChart3 className="w-4 h-4" />,
  };
  
  const productColors = {
    amber: 'text-amber-400',
    blue: 'text-blue-400',
    emerald: 'text-emerald-400',
    cyan: 'text-cyan-400',
    violet: 'text-violet-400',
  };
  
  return (
    <div className={`p-4 rounded-xl bg-gradient-to-r ${colors.bg} border-2 ${colors.border}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-black text-white flex items-center gap-2">
          <Gift className="w-5 h-5 text-emerald-400" />
          Бонусы в подарок
        </h3>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40">
          <span className="text-emerald-400 font-black text-sm">${totalBonusValue}</span>
          <span className="text-emerald-400/70 text-xs">в подарок</span>
        </div>
      </div>
      
      <p className="text-zinc-400 text-xs mb-4">
        Купив этот тариф, вы получаете доступ ко всем продуктам бесплатно:
      </p>
      
      <div className="space-y-2">
        {bonusProducts.map((product) => (
          <a 
            key={product.id}
            href={`/catalog#${product.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-800/50 border border-zinc-700/50 hover:border-zinc-600 hover:bg-zinc-800/80 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg bg-zinc-700/50 flex items-center justify-center ${productColors[product.color]}`}>
                {iconMap[product.icon]}
              </div>
              <div>
                <p className="text-white font-semibold text-sm group-hover:text-amber-400 transition-colors">{product.name}</p>
                <p className="text-zinc-500 text-xs">{product.desc}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-zinc-500 text-xs line-through">${product.price}</p>
              <p className="text-emerald-400 font-bold text-sm">Бесплатно</p>
            </div>
          </a>
        ))}
      </div>
      
      <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
        <p className="text-emerald-400 font-bold text-sm text-center">
          Общая стоимость бонусов: <span className="text-white">${totalBonusValue}</span> — для вас <span className="text-emerald-300">БЕСПЛАТНО</span>
        </p>
      </div>
    </div>
  );
};

// Flow Modal Component - "Что будет в потоке?"
const FlowModal = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { getPrice } = usePrices();
  
  // Calculate total bonus value
  const bonusProducts = bonusProductsConfig.map(p => ({
    ...p,
    price: getPrice(p.priceKey)
  }));
  const totalBonusValue = bonusProducts.reduce((sum, p) => sum + (p.price || 0), 0);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsVisible(false);
    // Unlock scroll immediately, then close modal after animation
    unlockBodyScroll();
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // 9 тем созвонов - реальная программа
  const callTopics = [
    { num: 1, title: 'Юридическое оформление', desc: 'Регистрация компании в ЕС или США' },
    { num: 2, title: 'Регистрация аккаунта', desc: 'Создание аккаунта без критических ошибок' },
    { num: 3, title: 'Поиск поставщиков', desc: 'Где искать надёжных поставщиков' },
    { num: 4, title: 'Выбор товаров', desc: 'Критерии отбора прибыльных товаров' },
    { num: 5, title: 'Глубокий анализ', desc: 'Анализ продаж и конкуренции' },
    { num: 6, title: 'Prep-центры', desc: 'Работа с преп-центрами, упаковка' },
    { num: 7, title: 'Отправка на Amazon', desc: 'Логистика и создание шипмента' },
    { num: 8, title: 'Аналитика', desc: 'Sellerboard, репрайсер, контроль' },
    { num: 9, title: 'Масштабирование', desc: 'Увеличение оборотов' },
  ];

  // Скриншоты
  const courseScreenshot = 'https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/cvbekj5n_image.png';
  const callScreenshots = [
    'https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/ix5yahu1_image.png',
    'https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/6ty8b2fn_image.png',
    'https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/fsju11zb_image.png',
  ];
  const groupChatScreenshot = 'https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/whu6dtgf_image.png';

  return createPortal(
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'bg-black/90 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`relative max-w-3xl w-full max-h-[90vh] overflow-auto transition-all duration-500 ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-8'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow */}
        <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/30 via-indigo-500/40 to-blue-500/30 rounded-[2rem] blur-2xl" />
        
        {/* Card */}
        <div className="relative bg-gradient-to-b from-zinc-800 to-zinc-900 rounded-[1.5rem] border border-blue-500/40 overflow-hidden shadow-2xl">
          
          {/* Header */}
          <div className="relative p-6 sm:p-8 bg-gradient-to-b from-blue-500/20 to-transparent border-b border-blue-500/20">
            <button 
              onClick={handleClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-xl shadow-blue-500/30">
                <Rocket className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-black text-white">Поток</h2>
                <p className="text-blue-300 text-sm">Групповое обучение с Вадимом</p>
              </div>
            </div>
            
            <p className="text-zinc-300 text-sm leading-relaxed">
              За <span className="text-white font-bold">1 месяц</span> вы получаете структурированную систему запуска бизнеса на Amazon — 
              вместо месяцев и лет самостоятельных экспериментов.
            </p>
          </div>

          {/* Content */}
          <div className="p-6 sm:p-8 space-y-6">
            
            {/* SkillSpace Section */}
            <div>
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Video className="w-5 h-5 text-blue-400" />
                Видео-курс на SkillSpace
              </h3>
              <p className="text-zinc-400 text-sm mb-3">
                После оплаты вы получите доступ к платформе SkillSpace с <a href="#modules" onClick={handleClose} className="text-blue-400 hover:underline font-semibold">75 видеоуроками</a> и домашними заданиями.
              </p>
              <div className="rounded-xl overflow-hidden border border-blue-500/30">
                <ZoomableImage src={courseScreenshot} alt="Структура курса" />
              </div>
              <a href="#modules" onClick={handleClose} className="inline-flex items-center gap-2 mt-3 text-blue-400 hover:underline font-semibold text-sm">
                <Play className="w-4 h-4" />
                Смотреть детальное расписание 75 уроков →
              </a>
            </div>
            
            {/* ASIN Checker Software - FREE */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-2 border-emerald-500/40">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Bot className="w-5 h-5 text-cyan-400" />
                  Авторский софт ASIN Checker
                </h3>
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 font-bold text-xs">БЕСПЛАТНО</span>
                </div>
              </div>
              <p className="text-zinc-300 text-sm mb-2">
                Софт для массовой проверки открытых и закрытых товаров на Amazon.
              </p>
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 mb-3">
                <p className="text-emerald-400 font-bold text-sm flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  В рамках этого тарифа софт для вас полностью бесплатный — доступ навсегда!
                </p>
                <p className="text-zinc-400 text-xs mt-1">Обычная стоимость: $350</p>
              </div>
              <a 
                href="/catalog#asin-checker" 
                onClick={handleClose}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-500/30 transition-colors"
              >
                <Search className="w-4 h-4" />
                Подробнее о софте
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            
            {/* Group Calls Explanation */}
            <div className="p-5 rounded-xl bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/30">
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                9 групповых созвонов
              </h3>
              <div className="space-y-3 text-zinc-300 text-sm">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><span className="text-white font-semibold">Раз в неделю</span> — созвон по расписанию</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><span className="text-white font-semibold">Каждый может задавать вопросы</span> — интерактивный формат</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><span className="text-white font-semibold">Записываются</span> — если пропустили, посмотрите запись</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span><span className="text-white font-semibold">Удобное время</span> — подбирается для всей группы</span>
                </div>
              </div>
              
              {/* Call screenshots - zoomable */}
              <p className="text-zinc-500 text-xs mt-4 mb-2">Нажмите на фото чтобы увеличить:</p>
              <div className="grid grid-cols-3 gap-2">
                {callScreenshots.map((src, i) => (
                  <ZoomableImage key={i} src={src} alt={`Созвон ${i+1}`} className="border border-zinc-700 rounded-lg" />
                ))}
              </div>
            </div>
            
            {/* Recording to group chat */}
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700/50">
              <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-violet-400" />
                После созвона
              </h4>
              <p className="text-zinc-400 text-sm mb-3">
                Запись каждого созвона сбрасывается в группу — вы всегда можете пересмотреть.
              </p>
              <ZoomableImage src={groupChatScreenshot} alt="Запись в группе" className="max-w-sm border border-zinc-700 rounded-lg" />
            </div>
            
            {/* 9 Call Topics */}
            <div>
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                План 9 созвонов
              </h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {callTopics.map((topic) => (
                  <div 
                    key={topic.num}
                    className="flex items-start gap-2 p-2 rounded-lg bg-zinc-800/50 border border-zinc-700/50"
                  >
                    <div className="w-6 h-6 rounded-md bg-blue-500/20 flex items-center justify-center flex-shrink-0 text-blue-400 font-bold text-xs">
                      {topic.num}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-xs">{topic.title}</p>
                      <p className="text-zinc-500 text-[10px]">{topic.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Bonus Products */}
            <BonusProductsSection onClose={handleClose} accentColor="blue" />
            
            {/* Summary - What you get */}
            <div className="p-5 rounded-xl bg-gradient-to-br from-blue-500/10 via-zinc-900 to-indigo-500/10 border-2 border-blue-500/40">
              <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-400" />
                После оплаты вы получите:
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center text-white font-bold text-sm">1</div>
                  <a href="#modules" onClick={handleClose} className="text-white font-medium text-sm hover:text-blue-400 transition-colors">Доступ к видео-курсу на SkillSpace (<span className="text-blue-400">75 уроков</span>) →</a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center text-white font-bold text-sm">2</div>
                  <span className="text-white font-medium text-sm">Все бонусные продукты <span className="text-emerald-400 font-bold">(${totalBonusValue} в подарок)</span></span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center text-white font-bold text-sm">3</div>
                  <span className="text-white font-medium text-sm">Доступ к чату в назначенную дату старта потока</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center text-white font-bold text-sm">4</div>
                  <span className="text-white font-medium text-sm">9 групповых созвонов по чёткому плану</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-white font-bold text-sm">5</div>
                  <span className="text-white font-medium text-sm">3 месяца поддержки после обучения</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center text-white font-bold text-sm">6</div>
                  <span className="text-white font-medium text-sm">Довод до первых продаж при старании ученика</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleClose}
              className="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-black text-lg rounded-xl hover:shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <span>Понятно!</span>
              <Check className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Countdown Timer Component — uses server-side deadline from PromoContext
const CountdownTimer = () => {
  const { remainingSeconds, isExpired, hasValidPromo, loaded } = usePromo();

  if (!loaded) return null;
  if (!hasValidPromo) return null;

  const total = Math.max(0, remainingSeconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="w-[3.75rem] sm:w-20 h-[3.75rem] sm:h-20 rounded-xl bg-gradient-to-b from-zinc-800 to-zinc-900 border border-amber-500/30 flex items-center justify-center shadow-inner shadow-amber-500/5">
        <span className="text-2xl sm:text-4xl font-black text-amber-400 tabular-nums">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-zinc-500 text-[0.65rem] sm:text-xs mt-1.5 sm:mt-2 uppercase tracking-wider">{label}</span>
    </div>
  );

  if (isExpired) {
    return (
      <div className="flex items-center justify-center gap-3" data-testid="countdown-expired">
        <span className="text-red-400 font-black text-xl">Время вышло</span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-4" data-testid="countdown-timer">
      <TimeBlock value={hours} label="часов" />
      <span className="text-2xl sm:text-3xl font-black text-amber-500 mb-5 sm:mb-6">:</span>
      <TimeBlock value={minutes} label="минут" />
      <span className="text-2xl sm:text-3xl font-black text-amber-500 mb-5 sm:mb-6">:</span>
      <TimeBlock value={seconds} label="секунд" />
    </div>
  );
};

// Детальная конфигурация тарифов с полными описаниями (без Супер-продвинутого)
const tierConfig = {
  base: {
    name: 'Обучаюсь сам',
    subtitle: 'Самостоятельное обучение',
    resultLine: 'Результат: знание процесса + старт без наставника',
    price: 700,
    color: 'zinc',
    available: true,
    cta: 'Начать обучение',
    features: [
      { text: 'Все 75 видео-уроков', included: true },
      { text: 'Авторские софты', included: true },
      { text: 'Доступ навсегда', included: true },
      { text: 'Записи созвонов', included: true },
      { text: 'Личные созвоны', included: false },
    ],
  },
  flow: {
    name: 'Поток',
    subtitle: 'Групповое обучение',
    resultLine: 'Результат: первые продажи + понимание процесса',
    price: 830,
    color: 'blue',
    available: true,
    cta: 'Забронировать место',
    features: [
      { text: 'Все 75 видео-уроков', included: true },
      { text: '9 групповых созвонов', included: true },
      { text: '2 личных созвона', included: true },
      { text: '1 поставщик для старта', included: true },
      { text: 'Поддержка в чате 24/7', included: true },
    ],
  },
  advanced: {
    name: 'Продвинутый',
    subtitle: 'С личным менторством',
    resultLine: 'Результат: стабильные продажи + система',
    price: 1500,
    color: 'violet',
    popular: true,
    available: true,
    cta: 'Начать обучение',
    features: [
      { text: 'Всё из тарифа Обучаюсь сам', included: true },
      { text: '4 личных созвона', included: true },
      { text: '3 поставщика для старта', included: true },
      { text: 'Выбор рынка: США или ЕС', included: true },
      { text: '4 месяца поддержки', included: true },
    ],
  },
  pro: {
    name: 'Про уровень',
    subtitle: 'Максимальный результат',
    resultLine: 'Результат: бизнес под ключ с целью $15K+ оборота/мес',
    price: 5800,
    color: 'emerald',
    premium: true,
    available: true,
    cta: 'Забронировать VIP',
    features: [
      { text: 'Всё из Продвинутого', included: true, highlight: true },
      { text: '12 личных созвонов', included: true },
      { text: '10 поставщиков + 50 товаров', included: true },
      { text: '2 сайта + CRM + воронка', included: true },
      { text: '6 месяцев личной поддержки', included: true },
    ],
  }
};

const colorStyles = {
  zinc: { 
    bg: 'from-zinc-800/50 to-zinc-900/50', 
    border: 'border-zinc-700/50', 
    text: 'text-zinc-400',
    button: 'bg-zinc-700 hover:bg-zinc-600',
    check: 'text-zinc-300',
    checkBg: 'bg-zinc-700/60',
    glow: '0 0 8px rgba(161, 161, 170, 0.4)'
  },
  blue: { 
    bg: 'from-blue-500/10 to-blue-900/20', 
    border: 'border-blue-500/30', 
    text: 'text-blue-400',
    button: 'bg-blue-600 hover:bg-blue-500',
    check: 'text-blue-300',
    checkBg: 'bg-blue-500/30',
    glow: '0 0 8px rgba(59, 130, 246, 0.5)'
  },
  violet: { 
    bg: 'from-violet-500/10 to-violet-900/20', 
    border: 'border-violet-500/30', 
    text: 'text-violet-400',
    button: 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500',
    check: 'text-violet-300',
    checkBg: 'bg-violet-500/30',
    glow: '0 0 8px rgba(139, 92, 246, 0.5)'
  },
  emerald: { 
    bg: 'from-emerald-500/10 to-emerald-900/20', 
    border: 'border-emerald-500/40', 
    text: 'text-emerald-400',
    button: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400',
    check: 'text-emerald-300',
    checkBg: 'bg-emerald-500/30',
    glow: '0 0 8px rgba(16, 185, 129, 0.5)'
  },
};

const FeatureRow = ({ feature, colors }) => {
  const isIncluded = feature.included === true;
  const isPartial = feature.included === 'partial';
  const isExcluded = feature.included === false;
  
  if (isExcluded) {
    return (
      <div className="flex items-start gap-2.5 opacity-40">
        <div className="w-5 h-5 rounded-full bg-zinc-800/60 flex items-center justify-center flex-shrink-0 mt-0.5">
          <X className="w-3 h-3 text-zinc-600" />
        </div>
        <span className="text-xs text-zinc-600 line-through leading-relaxed">{feature.text}</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-start gap-2.5">
      <div 
        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${colors.checkBg}`}
        style={{ boxShadow: colors.glow }}
      >
        <Check className={`w-3 h-3 ${colors.check}`} />
      </div>
      <span className={`text-xs leading-relaxed ${feature.highlight ? `${colors.text} font-semibold` : 'text-zinc-300'}`}>
        {feature.text}
        {feature.note && <span className="text-zinc-500 ml-1">{feature.note}</span>}
      </span>
    </div>
  );
};

// Package ID mapping
const packageIdMap = {
  base: 'base',
  flow: 'flow',
  advanced: 'advanced',
  pro: 'pro',
};

// === МОДАЛЬНЫЕ ОКНА "ЧТО ВХОДИТ" ДЛЯ КАЖДОГО ТАРИФА ===

// Общие темы 9 созвонов
const callTopics = [
  { num: 1, title: 'Юридическое оформление', desc: 'Регистрация компании в ЕС или США' },
  { num: 2, title: 'Регистрация аккаунта', desc: 'Создание аккаунта без критических ошибок' },
  { num: 3, title: 'Поиск поставщиков', desc: 'Где искать надёжных поставщиков' },
  { num: 4, title: 'Выбор товаров', desc: 'Критерии отбора прибыльных товаров' },
  { num: 5, title: 'Глубокий анализ', desc: 'Анализ продаж и конкуренции' },
  { num: 6, title: 'Prep-центры', desc: 'Работа с преп-центрами, упаковка' },
  { num: 7, title: 'Отправка на Amazon', desc: 'Логистика и создание шипмента' },
  { num: 8, title: 'Аналитика', desc: 'Sellerboard, репрайсер, контроль' },
  { num: 9, title: 'Масштабирование', desc: 'Увеличение оборотов' },
];

// Скриншот курса
const courseScreenshot = 'https://customer-assets.emergentagent.com/job_amazon-mega-course/artifacts/cvbekj5n_image.png';

// Модальное окно "Что входит" для БАЗОВОГО тарифа
const BaseIncludesModal = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { getPrice } = usePrices();
  
  // Calculate total bonus value
  const bonusProducts = bonusProductsConfig.map(p => ({
    ...p,
    price: getPrice(p.priceKey)
  }));
  const totalBonusValue = bonusProducts.reduce((sum, p) => sum + (p.price || 0), 0);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsVisible(false);
    // Unlock scroll immediately, then close modal after animation
    unlockBodyScroll();
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return createPortal(
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'bg-black/90 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`relative max-w-2xl w-full max-h-[90vh] overflow-auto transition-all duration-500 ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-8'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow */}
        <div className="absolute -inset-2 bg-gradient-to-r from-amber-500/30 via-zinc-400/40 to-amber-500/30 rounded-[2rem] blur-2xl" />
        
        {/* Card */}
        <div className="relative bg-gradient-to-b from-zinc-800 to-zinc-900 rounded-[1.5rem] border border-amber-500/30 overflow-hidden shadow-2xl">
          
          {/* Header */}
          <div className="relative p-5 sm:p-8 bg-gradient-to-b from-amber-500/10 to-transparent border-b border-amber-500/20">
            <button 
              onClick={handleClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors z-10"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            <div className="flex items-center gap-3 sm:gap-4 mb-4 pr-10">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-xl shadow-amber-500/30 flex-shrink-0">
                <Package className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-3xl font-black text-white">Обучаюсь сам</h2>
                <p className="text-zinc-400 text-xs sm:text-sm">Что входит в тариф</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 sm:p-8 space-y-5 sm:space-y-6">
            
            {/* Intro */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30">
              <p className="text-zinc-200 text-sm sm:text-base leading-relaxed">
                После оплаты вы получите <span className="text-white font-bold">полный доступ к кабинету SkillSpace</span> — 
                платформе с видеоматериалами и домашними заданиями.
              </p>
            </div>
            
            {/* Course Screenshot */}
            <div>
              <h3 className="text-base sm:text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Video className="w-5 h-5 text-amber-400" />
                Как выглядит курс
              </h3>
              <div className="rounded-xl overflow-hidden border border-zinc-700/50">
                <ZoomableImage src={courseScreenshot} alt="Структура курса" />
              </div>
              <a href="#modules" onClick={handleClose} className="inline-flex items-center gap-2 mt-3 text-amber-400 hover:underline font-semibold text-sm sm:text-base">
                <Play className="w-4 h-4" />
                Смотреть детальное расписание 75 уроков →
              </a>
            </div>
            
            {/* Software - FREE */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border-2 border-emerald-500/40">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-base sm:text-lg font-bold text-white flex items-center gap-2">
                  <Bot className="w-5 h-5 text-cyan-400" />
                  Авторский софт ASIN Checker
                </h3>
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-400 font-bold text-xs">БЕСПЛАТНО</span>
                </div>
              </div>
              <p className="text-zinc-300 text-sm sm:text-base mb-2">
                Софт для проверки открытых и закрытых товаров на Amazon.
              </p>
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 mb-3">
                <p className="text-emerald-400 font-bold text-sm sm:text-base flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  В рамках этого тарифа софт для вас полностью бесплатный — доступ навсегда!
                </p>
                <p className="text-zinc-400 text-xs sm:text-sm mt-1">Обычная стоимость: $350</p>
              </div>
              <a 
                href="/catalog#asin-checker" 
                onClick={handleClose}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-500/30 transition-colors"
              >
                <Search className="w-4 h-4" />
                Подробнее о софте
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            
            {/* Call Recordings */}
            <div>
              <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                <Play className="w-5 h-5 text-emerald-400" />
                Записи 9 созвонов с прошлых потоков
              </h3>
              <p className="text-zinc-400 text-sm mb-3">
                Вы получите полные записи всех групповых созвонов. Вот что мы обсуждаем:
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                {callTopics.map((topic) => (
                  <div 
                    key={topic.num}
                    className="flex items-start gap-2 p-2 rounded-lg bg-zinc-800/50 border border-zinc-700/50"
                  >
                    <div className="w-6 h-6 rounded-md bg-emerald-500/20 flex items-center justify-center flex-shrink-0 text-emerald-400 font-bold text-xs">
                      {topic.num}
                    </div>
                    <div>
                      <p className="text-white font-semibold text-xs">{topic.title}</p>
                      <p className="text-zinc-500 text-[10px]">{topic.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Support */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30">
              <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-violet-400" />
                Поддержка в чате — 1 месяц
              </h3>
              <p className="text-zinc-300 text-sm">
                Любой вопрос по Amazon или обучению — просто пишете в чат Вадиму и получаете ответ.
                <span className="text-zinc-500 block mt-1">Личных созвонов в этом тарифе нет.</span>
              </p>
            </div>
            
            {/* Bonus Products */}
            <BonusProductsSection onClose={handleClose} accentColor="amber" />
            
            {/* Summary */}
            <div className="p-5 rounded-xl bg-gradient-to-br from-amber-500/10 via-zinc-900 to-emerald-500/10 border-2 border-amber-500/30">
              <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-amber-400" />
                Итого после оплаты вы получите:
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <Video className="w-4 h-4 text-amber-400" />
                  </div>
                  <a href="#modules" onClick={handleClose} className="text-white font-medium text-sm hover:text-amber-400 transition-colors">
                    Доступ к платформе с <span className="text-amber-400">75 видеоуроками</span> →
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Gift className="w-4 h-4 text-emerald-400" />
                  </div>
                  <span className="text-white font-medium text-sm">Все бонусные продукты <span className="text-emerald-400 font-bold">(${totalBonusValue} в подарок)</span></span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Play className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-white font-medium text-sm">Записи 9 созвонов в чат</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                    <MessageCircle className="w-4 h-4 text-violet-400" />
                  </div>
                  <span className="text-white font-medium text-sm">1 месяц поддержки в чате</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleClose}
              className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-black text-lg rounded-xl hover:shadow-lg hover:shadow-amber-500/30 hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <span>Понятно!</span>
              <Check className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Модальное окно "Что входит" для тарифа ПРОДВИНУТЫЙ
const AdvancedIncludesModal = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const { getPrice } = usePrices();
  
  // Calculate total bonus value
  const bonusProducts = bonusProductsConfig.map(p => ({
    ...p,
    price: getPrice(p.priceKey)
  }));
  const totalBonusValue = bonusProducts.reduce((sum, p) => sum + (p.price || 0), 0);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsVisible(false);
    // Unlock scroll immediately, then close modal after animation
    unlockBodyScroll();
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return createPortal(
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'bg-black/90 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`relative max-w-2xl w-full max-h-[90vh] overflow-auto transition-all duration-500 ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-8'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -inset-2 bg-gradient-to-r from-violet-500/30 via-purple-500/40 to-violet-500/30 rounded-[2rem] blur-2xl" />
        
        <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-[1.5rem] border border-violet-500/30 overflow-hidden">
          
          <div className="relative p-5 sm:p-8 bg-gradient-to-b from-violet-500/20 to-transparent border-b border-violet-500/20">
            <button 
              onClick={handleClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors z-10"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            <div className="flex items-center gap-3 sm:gap-4 mb-4 pr-10">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-xl shadow-violet-500/30 flex-shrink-0">
                <UserCheck className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h2 className="text-xl sm:text-3xl font-black text-white">Продвинутый</h2>
                  <span className="px-2 py-0.5 rounded-full bg-violet-500/30 text-violet-300 text-xs font-bold">ПОПУЛЯРНЫЙ</span>
                </div>
                <p className="text-violet-300 text-xs sm:text-sm">Что входит в тариф</p>
              </div>
            </div>
          </div>

          <div className="p-5 sm:p-8 space-y-5">
            
            {/* Intro */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30">
              <p className="text-zinc-200 text-sm leading-relaxed">
                Включает <span className="text-violet-400 font-bold">всё из тарифа «Обучаюсь сам»</span> + 
                личное менторство и ресурсы для быстрого старта.
              </p>
            </div>
            
            {/* Includes from base tier */}
            <div className="p-4 rounded-xl bg-zinc-800/30 border border-zinc-700/50">
              <p className="text-zinc-400 text-xs mb-3 uppercase tracking-wider font-semibold">Включено из базового тарифа:</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-violet-400" />
                  <a href="#modules" onClick={handleClose} className="text-white hover:text-violet-400">Видео-курс <span className="text-violet-400">75 уроков</span> →</a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-white">ASIN Checker <span className="text-emerald-400 font-bold">(бесплатно)</span></span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-amber-400" />
                  <span className="text-white">9 записей созвонов</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-blue-400" />
                  <span className="text-white">Выбор рынка (США/ЕС)</span>
                </div>
              </div>
            </div>
            
            {/* 3 Suppliers */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Package className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white font-black text-xl">3 поставщика для старта</p>
                </div>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">
                Получите <span className="text-emerald-400 font-semibold">3 оптовика</span>, которые одобрят вам аккаунт и возможность закупиться от <span className="text-white font-bold">$4,000</span> с ROI от <span className="text-emerald-400 font-bold">20%</span>.
              </p>
            </div>
            
            {/* 4 Personal Calls */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-purple-500/10 border border-violet-500/30">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-white font-black text-xl">4 личных созвона</p>
                  <p className="text-violet-400 text-xs font-semibold">по 1.5 часа каждый</p>
                </div>
              </div>
              <p className="text-zinc-300 text-sm leading-relaxed">
                Получите возможность сделать <span className="text-violet-400 font-semibold">4 личных созвона с Вадимом</span>, задавая любые вопросы касательно Amazon, разбирая ваш кабинет и вашу конкретную ситуацию <span className="text-white font-semibold">прямо здесь и сейчас</span>.
              </p>
            </div>
            
            {/* 4 months support */}
            <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-amber-400" />
                </div>
                <p className="text-white font-black text-xl">4 месяца поддержки в чате</p>
              </div>
              <p className="text-zinc-300 text-sm">
                Любой вопрос по Amazon — пишете в чат и получаете ответ от Вадима.
              </p>
            </div>
            
            {/* Additional benefits */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Play className="w-4 h-4 text-blue-400" />
                </div>
                <span className="text-white font-medium text-sm">9 записей групповых созвонов</span>
              </div>
              <div className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Target className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-white font-medium text-sm">Довод до первых продаж</span>
              </div>
              <div className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-rose-400" />
                </div>
                <span className="text-white font-medium text-sm">Немедленный старт</span>
              </div>
              <div className="p-3 rounded-xl bg-zinc-800/50 border border-zinc-700/50 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <Video className="w-4 h-4 text-amber-400" />
                </div>
                <a href="#modules" onClick={handleClose} className="text-white font-medium text-sm hover:text-amber-400">75 видео-уроков →</a>
              </div>
            </div>
            
            {/* Bonus Products */}
            <BonusProductsSection onClose={handleClose} accentColor="violet" />

            {/* Result */}
            <div className="p-5 rounded-xl bg-gradient-to-br from-violet-500/10 via-zinc-900 to-purple-500/10 border-2 border-violet-500/30">
              <h3 className="text-lg font-black text-white mb-2">Результат:</h3>
              <p className="text-violet-300 font-bold text-lg">Стабильные продажи + выстроенная система</p>
            </div>

            <button
              onClick={handleClose}
              className="w-full py-4 px-6 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-black text-lg rounded-xl hover:from-violet-500 hover:to-purple-500 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <span>Понятно!</span>
              <Check className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Модальное окно "Что входит" для тарифа ПРО
const ProIncludesModal = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsVisible(false);
    // Unlock scroll immediately, then close modal after animation
    unlockBodyScroll();
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return createPortal(
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'bg-black/90 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`relative max-w-2xl w-full max-h-[90vh] overflow-auto transition-all duration-500 ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-8'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -inset-2 bg-gradient-to-r from-emerald-500/30 via-teal-500/40 to-emerald-500/30 rounded-[2rem] blur-2xl" />
        
        <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 rounded-[1.5rem] border border-emerald-500/40 overflow-hidden">
          
          <div className="relative p-6 sm:p-8 bg-gradient-to-b from-emerald-500/20 to-transparent border-b border-emerald-500/20">
            <button 
              onClick={handleClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                <Crown className="w-7 h-7 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-2xl sm:text-3xl font-black text-white">Про уровень</h2>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/30 text-emerald-300 text-xs font-bold">VIP</span>
                </div>
                <p className="text-emerald-300 text-sm">Бизнес под ключ</p>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-6">
            
            <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30">
              <p className="text-zinc-200 text-sm leading-relaxed">
                Включает <span className="text-emerald-400 font-bold">всё из тарифа «Продвинутый»</span> + 
                максимальный уровень сопровождения для построения бизнеса с целью $15K+ оборота/месяц.
              </p>
            </div>
            
            {/* Includes from previous tiers */}
            <div className="p-4 rounded-xl bg-zinc-800/30 border border-zinc-700/50">
              <p className="text-zinc-400 text-xs mb-3 uppercase tracking-wider font-semibold">Включено из предыдущих тарифов:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <a href="#modules" onClick={handleClose} className="text-white hover:text-emerald-400">Видео-курс <span className="text-emerald-400">75 уроков</span> →</a>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-teal-400" />
                  <span className="text-white">ASIN Checker <span className="text-teal-400 font-bold">(БЕСПЛАТНО, навсегда)</span></span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-amber-400" />
                  <span className="text-white">Записи 9 созвонов + 4 личных созвона</span>
                </div>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl bg-zinc-800/50 border border-emerald-500/30">
                <Phone className="w-8 h-8 text-emerald-400 mb-2" />
                <p className="text-white font-black text-2xl">12</p>
                <p className="text-zinc-400 text-sm">личных созвонов</p>
              </div>
              <div className="p-4 rounded-xl bg-zinc-800/50 border border-emerald-500/30">
                <Package className="w-8 h-8 text-teal-400 mb-2" />
                <p className="text-white font-black text-2xl">10+50</p>
                <p className="text-zinc-400 text-sm">поставщиков + товаров</p>
              </div>
              <div className="p-4 rounded-xl bg-zinc-800/50 border border-emerald-500/30">
                <Globe className="w-8 h-8 text-blue-400 mb-2" />
                <p className="text-white font-black text-lg">2 сайта + CRM</p>
                <p className="text-zinc-400 text-sm">+ воронка продаж</p>
              </div>
              <div className="p-4 rounded-xl bg-zinc-800/50 border border-emerald-500/30">
                <MessageCircle className="w-8 h-8 text-amber-400 mb-2" />
                <p className="text-white font-black text-2xl">6 мес</p>
                <p className="text-zinc-400 text-sm">личной поддержки</p>
              </div>
            </div>
            
            {/* Bonus Products */}
            <BonusProductsSection onClose={handleClose} accentColor="emerald" />
            
            {/* VIP Exclusive Bonuses - Website + CRM */}
            <div className="p-5 rounded-xl bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 border-2 border-amber-500/40 relative overflow-hidden">
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-500/10 to-transparent animate-shimmer" style={{backgroundSize: '200% 100%'}} />
              
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-black text-white flex items-center gap-2">
                    <Crown className="w-5 h-5 text-amber-400" />
                    VIP Эксклюзив
                  </h3>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/40">
                    <span className="text-amber-400 font-black text-sm">$3,000+</span>
                    <span className="text-amber-400/70 text-xs">в подарок</span>
                  </div>
                </div>
                
                <p className="text-zinc-400 text-xs mb-4">
                  Только для тарифа «Про уровень» — эксклюзивные бонусы:
                </p>
                
                <div className="space-y-3">
                  {/* Custom Website */}
                  <div className="p-4 rounded-xl bg-zinc-800/70 border border-amber-500/30 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
                        <Globe className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-white font-bold">Кастомный веб-сайт</p>
                          <div className="text-right">
                            <p className="text-zinc-500 text-xs line-through">$1,500+</p>
                            <p className="text-emerald-400 font-bold text-sm">Бесплатно</p>
                          </div>
                        </div>
                        <p className="text-zinc-400 text-sm leading-relaxed">
                          Персональный сайт с <span className="text-amber-400">сегментацией аудитории</span>, 
                          интеграцией оплаты и <span className="text-amber-400">премиум-дизайном</span>. 
                          Идеально для оптовиков — привлекайте клиентов профессионально.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Custom CRM */}
                  <div className="p-4 rounded-xl bg-zinc-800/70 border border-amber-500/30 hover:border-amber-500/50 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                        <BarChart3 className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-white font-bold">CRM-система под вас</p>
                          <div className="text-right">
                            <p className="text-zinc-500 text-xs line-through">$1,500+</p>
                            <p className="text-emerald-400 font-bold text-sm">Бесплатно</p>
                          </div>
                        </div>
                        <p className="text-zinc-400 text-sm leading-relaxed">
                          Кастомная CRM для <span className="text-violet-400">контроля сотрудников</span>, 
                          учёта брендов, постановки задач, заметок, чата и уведомлений. 
                          <span className="text-violet-400">Мощная база данных</span> для контроля воронки с каждым поставщиком.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-amber-400 font-bold text-sm text-center">
                    Общая стоимость VIP-бонусов: <span className="text-white">$3,000+</span> — для вас <span className="text-emerald-400">БЕСПЛАТНО</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 rounded-xl bg-gradient-to-br from-emerald-500/10 via-zinc-900 to-teal-500/10 border-2 border-emerald-500/40">
              <h3 className="text-lg font-black text-white mb-3">Цель:</h3>
              <p className="text-emerald-300 font-bold text-xl">$15,000+ оборота/месяц</p>
              <p className="text-zinc-400 text-sm mt-1">Бизнес под ключ с полным сопровождением</p>
            </div>

            <button
              onClick={handleClose}
              className="w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-black text-lg rounded-xl hover:from-emerald-400 hover:to-teal-400 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <span>Понятно!</span>
              <Check className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// FLOW Roadmap Modal - "Roadmap тарифа Поток"
const FlowRoadmapModal = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);
  const modalContentRef = useRef(null);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
      setExpandedStage(null);
    }
  }, [isOpen]);
  
  // Scroll to top when modal becomes visible
  useEffect(() => {
    if (isVisible && modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [isVisible]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsVisible(false);
    // Unlock scroll immediately, then close modal after animation
    unlockBodyScroll();
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const toggleStage = (stageNum) => {
    setExpandedStage(expandedStage === stageNum ? null : stageNum);
  };

  // 6 этапов FLOW Roadmap (0-5)
  const stages = [
    {
      num: 0,
      title: 'Сразу после оплаты',
      subtitle: 'День 0',
      icon: <Gift className="w-5 h-5" />,
      color: 'emerald',
      items: [
        'Вы получаете доступ к SkillSpace: 75 уроков + домашние задания',
        'Открывается доступ ко всем бонусам ($1750)',
        'Вы получаете ASIN Checker (доступ навсегда)',
        'Приходит инструкция: как проходить курс + как подготовиться к старту потока',
      ]
    },
    {
      num: 1,
      title: 'Подготовка к запуску',
      subtitle: 'Недели 1–2',
      icon: <Rocket className="w-5 h-5" />,
      color: 'blue',
      items: [
        'Созвон №1: Юридическое оформление (компания ЕС/США)',
        'Созвон №2: Регистрация аккаунта без критических ошибок',
        'В чате: ответы на вопросы + проверка ваших шагов',
        'Домашние задания: собрать базу документов / настроить основу под аккаунт',
      ]
    },
    {
      num: 2,
      title: 'Поставщики и выбор товаров',
      subtitle: 'Недели 3–5',
      icon: <Package className="w-5 h-5" />,
      color: 'amber',
      items: [
        'Созвон №3: Поиск поставщиков',
        'Созвон №4: Выбор товаров (критерии прибыльности)',
        'Созвон №5: Глубокий анализ (спрос, конкуренция, риски)',
        'Практика: собрать шорт-лист товаров + проверить через ASIN Checker',
      ]
    },
    {
      num: 3,
      title: 'Операционка и отправка',
      subtitle: 'Недели 6–7',
      icon: <ShoppingCart className="w-5 h-5" />,
      color: 'violet',
      items: [
        'Созвон №6: Prep-центры (упаковка/маркировка)',
        'Созвон №7: Отправка на Amazon (логистика, shipment)',
        'Практика: подготовить поставку и оформить отправку',
      ]
    },
    {
      num: 4,
      title: 'Первые продажи и контроль цифр',
      subtitle: 'Недели 8–9',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'rose',
      items: [
        'Созвон №8: Аналитика (Sellerboard, репрайсер, контроль)',
        'Созвон №9: Масштабирование (как увеличивать оборот)',
        'Практика: настроить контроль прибыли и правила репрайса',
      ],
      highlight: 'На этом этапе у вас уже есть первые продажи и понимание цифр'
    },
    {
      num: 5,
      title: 'После потока',
      subtitle: 'Ещё 3 месяца',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'teal',
      items: [
        '3 месяца поддержки: чат + разбор вопросов',
        'Вы продолжаете закупки и повторяете цикл уже самостоятельно',
        'Цель: довести до первых продаж при выполнении шагов',
      ]
    },
  ];

  const colorClasses = {
    emerald: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-400', activeBg: 'bg-emerald-500/30' },
    blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-400', activeBg: 'bg-blue-500/30' },
    amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-400', activeBg: 'bg-amber-500/30' },
    violet: { bg: 'bg-violet-500/20', border: 'border-violet-500/40', text: 'text-violet-400', activeBg: 'bg-violet-500/30' },
    rose: { bg: 'bg-rose-500/20', border: 'border-rose-500/40', text: 'text-rose-400', activeBg: 'bg-rose-500/30' },
    teal: { bg: 'bg-teal-500/20', border: 'border-teal-500/40', text: 'text-teal-400', activeBg: 'bg-teal-500/30' },
  };

  return createPortal(
    <div 
      className={`fixed inset-0 z-[100] flex items-start sm:items-center justify-center pt-4 sm:pt-0 transition-all duration-300 ${
        isVisible ? 'bg-black/90 backdrop-blur-sm' : 'bg-black/0 pointer-events-none'
      }`}
      onClick={handleClose}
    >
      <div 
        ref={modalContentRef}
        className={`relative w-full sm:max-w-2xl sm:mx-4 max-h-[96vh] sm:max-h-[90vh] overflow-auto transition-all duration-500 rounded-2xl sm:rounded-[1.5rem] ${
          isVisible 
            ? 'opacity-100 translate-y-0 sm:scale-100' 
            : 'opacity-0 -translate-y-full sm:translate-y-8 sm:scale-95'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow - only on desktop */}
        <div className="hidden sm:block absolute -inset-2 bg-gradient-to-r from-blue-500/30 via-indigo-500/40 to-blue-500/30 rounded-[2rem] blur-2xl" />
        
        {/* Card */}
        <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 sm:rounded-[1.5rem] rounded-t-[1.5rem] border-t sm:border border-blue-500/40 overflow-hidden">
          
          {/* Header */}
          <div className="p-4 sm:p-6 bg-gradient-to-b from-blue-500/10 to-transparent border-b border-blue-500/20">
            <button 
              onClick={handleClose}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors z-20"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>

            <div className="flex items-center gap-3 mb-3 pr-10 sm:pr-12">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30 flex-shrink-0">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-2xl font-black text-white">Roadmap «Поток»</h2>
                <p className="text-blue-400 text-xs sm:text-sm">Групповое обучение за 9 недель</p>
              </div>
            </div>
            
            <p className="text-zinc-400 text-xs sm:text-sm">
              Нажмите на этап, чтобы увидеть детали
            </p>
          </div>

          {/* Content - 6 Stages Accordion */}
          <div className="p-4 sm:p-6 space-y-3">
            {stages.map((stage) => {
              const colors = colorClasses[stage.color];
              const isExpanded = expandedStage === stage.num;
              
              return (
                <div 
                  key={stage.num}
                  className={`rounded-xl border transition-all duration-300 ${
                    isExpanded 
                      ? `${colors.border} ${colors.activeBg}` 
                      : 'border-zinc-700/50 bg-zinc-800/30 hover:border-zinc-600'
                  }`}
                >
                  {/* Stage Header - Clickable */}
                  <button
                    onClick={() => toggleStage(stage.num)}
                    className="w-full flex items-center gap-3 p-4 text-left"
                  >
                    <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center ${colors.text} flex-shrink-0`}>
                      {stage.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold ${colors.text}`}>ЭТАП {stage.num}</span>
                        {stage.subtitle && (
                          <span className="text-zinc-500 text-xs">• {stage.subtitle}</span>
                        )}
                      </div>
                      <p className="text-white font-bold text-base sm:text-lg truncate">{stage.title}</p>
                    </div>
                    <ChevronDown 
                      className={`w-5 h-5 text-zinc-400 flex-shrink-0 transition-transform duration-300 ${
                        isExpanded ? 'rotate-180' : ''
                      }`} 
                    />
                  </button>
                  
                  {/* Stage Content - Expandable */}
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${
                      isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-4 pb-4 pt-0">
                      <div className="space-y-2 ml-[52px]">
                        {stage.items.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <Check className={`w-4 h-4 ${colors.text} flex-shrink-0 mt-0.5`} />
                            <span className="text-zinc-300 text-sm">{item}</span>
                          </div>
                        ))}
                        {stage.highlight && (
                          <div className={`mt-3 p-3 rounded-lg ${colors.bg} border ${colors.border}`}>
                            <p className={`${colors.text} text-sm font-semibold flex items-start gap-2`}>
                              <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              {stage.highlight}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Final Result Block */}
            <div className="mt-6 p-5 rounded-xl bg-gradient-to-br from-blue-500/10 via-zinc-900 to-indigo-500/10 border-2 border-blue-500/40">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-5 h-5 text-blue-400" />
                <h3 className="text-lg font-black text-white">Что вы получите в итоге</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-blue-400" />
                  <span className="text-zinc-300 text-sm">Понятную систему запуска Amazon-бизнеса</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-blue-400" />
                  <span className="text-zinc-300 text-sm">Навык: поставщики → товар → отправка → контроль прибыли</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-blue-400" />
                  <span className="text-zinc-300 text-sm">Записи всех 9 созвонов + доступ к урокам и материалам</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-white text-sm font-semibold">Инструменты: ASIN Checker + бонусные продукты ($1750)</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleClose}
              className="w-full py-4 px-6 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-black text-lg rounded-xl hover:from-blue-400 hover:to-indigo-500 transition-all duration-300 flex items-center justify-center gap-2 mt-4"
            >
              <span>Понятно!</span>
              <Check className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// PRO Roadmap Modal - "Как мы запустим ваш Amazon-бизнес"
const ProRoadmapModal = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedStage, setExpandedStage] = useState(null);
  const modalContentRef = useRef(null);
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setIsVisible(true), 50);
    } else {
      setIsVisible(false);
      setExpandedStage(null);
    }
  }, [isOpen]);
  
  // Scroll to top when modal becomes visible
  useEffect(() => {
    if (isVisible && modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [isVisible]);

  if (!isOpen) return null;

  const handleClose = () => {
    setIsVisible(false);
    // Unlock scroll immediately, then close modal after animation
    unlockBodyScroll();
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const toggleStage = (stageNum) => {
    setExpandedStage(expandedStage === stageNum ? null : stageNum);
  };

  // 5 этапов PRO Roadmap
  const stages = [
    {
      num: 1,
      title: 'Старт и стратегия',
      icon: <Rocket className="w-5 h-5" />,
      color: 'emerald',
      items: [
        'Доступ к 75 видео-урокам',
        'Назначается персональный созвон для оценки ситуации',
        'Помощь с документами, компанией, контактами',
        'Доступ к записям 9 созвонов',
        'Формируется индивидуальный план действий',
      ]
    },
    {
      num: 2,
      title: 'Создание инфраструктуры',
      icon: <Settings className="w-5 h-5" />,
      color: 'blue',
      items: [
        'Начинаем создание веб-сайта (под ваши цели)',
        'Запускаем разработку CRM-системы (кастом / аналог нашей)',
        'Настраиваем Google-таблицы',
        'Настраиваем Sellerboard',
        'Настраиваем финансовый контроль',
      ]
    },
    {
      num: 3,
      title: 'Поставщики и товары',
      icon: <Package className="w-5 h-5" />,
      color: 'amber',
      items: [
        'Доступ к проверенным поставщикам',
        'Совместный созвон: регистрация, разбор условий',
        'Анализ каталогов вместе',
        'Выбор прибыльных товаров',
        'Подбор преп-центра',
        'Выкуп партий',
      ]
    },
    {
      num: 4,
      title: 'Amazon и продажи',
      icon: <ShoppingCart className="w-5 h-5" />,
      color: 'violet',
      items: [
        'Ангейтинг товаров + открытие брендов',
        'Отправка товара на склады Amazon',
        'Созвон по репрайсеру',
        'Настройка под ваш аккаунт',
        'Появляются первые продажи',
      ],
      highlight: 'На этом этапе у вас уже есть активные листинги и движение денег'
    },
    {
      num: 5,
      title: 'Самостоятельность и масштабирование',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'rose',
      items: [
        'Созвоны по аналитике товаров и поиску поставщиков',
        'Практические задания: вы анализируете — мы проверяем и даём видео-фидбек',
        'Самостоятельный поиск поставщика',
        'Вторая закупка уже по вашей инициативе',
        'Повтор цикла без нашей прямой помощи',
      ]
    },
  ];

  const colorClasses = {
    emerald: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-400', activeBg: 'bg-emerald-500/30' },
    blue: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-400', activeBg: 'bg-blue-500/30' },
    amber: { bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-400', activeBg: 'bg-amber-500/30' },
    violet: { bg: 'bg-violet-500/20', border: 'border-violet-500/40', text: 'text-violet-400', activeBg: 'bg-violet-500/30' },
    rose: { bg: 'bg-rose-500/20', border: 'border-rose-500/40', text: 'text-rose-400', activeBg: 'bg-rose-500/30' },
  };

  return createPortal(
    <div 
      className={`fixed inset-0 z-[100] flex items-start sm:items-center justify-center pt-4 sm:pt-0 transition-all duration-300 ${
        isVisible ? 'bg-black/90 backdrop-blur-sm' : 'bg-black/0 pointer-events-none'
      }`}
      onClick={handleClose}
    >
      <div 
        ref={modalContentRef}
        className={`relative w-full sm:max-w-2xl sm:mx-4 max-h-[96vh] sm:max-h-[90vh] overflow-auto transition-all duration-500 rounded-2xl sm:rounded-[1.5rem] ${
          isVisible 
            ? 'opacity-100 translate-y-0 sm:scale-100' 
            : 'opacity-0 -translate-y-full sm:translate-y-8 sm:scale-95'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Glow - only on desktop */}
        <div className="hidden sm:block absolute -inset-2 bg-gradient-to-r from-emerald-500/30 via-teal-500/40 to-emerald-500/30 rounded-[2rem] blur-2xl" />
        
        {/* Card - mobile fullscreen / desktop modal */}
        <div className="relative bg-gradient-to-b from-zinc-900 to-zinc-950 sm:rounded-[1.5rem] rounded-t-[1.5rem] border-t sm:border border-emerald-500/40 overflow-hidden">
          
          {/* Header */}
          <div className="p-5 sm:p-6 bg-gradient-to-b from-zinc-900 via-zinc-900 to-zinc-900/95 border-b border-emerald-500/20">
            <button 
              onClick={handleClose}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors z-20"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-3 pr-12">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <Map className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-white">Roadmap PRO</h2>
                <p className="text-emerald-400 text-xs sm:text-sm">Как мы запустим ваш Amazon-бизнес</p>
              </div>
            </div>
            
            <p className="text-zinc-400 text-sm">
              Нажмите на этап, чтобы увидеть детали
            </p>
          </div>

          {/* Content - 5 Stages Accordion */}
          <div className="p-4 sm:p-6 space-y-3">
            {stages.map((stage) => {
              const colors = colorClasses[stage.color];
              const isExpanded = expandedStage === stage.num;
              
              return (
                <div 
                  key={stage.num}
                  className={`rounded-xl border transition-all duration-300 ${
                    isExpanded 
                      ? `${colors.border} ${colors.activeBg}` 
                      : 'border-zinc-700/50 bg-zinc-800/30 hover:border-zinc-600'
                  }`}
                >
                  {/* Stage Header - Clickable */}
                  <button
                    onClick={() => toggleStage(stage.num)}
                    className="w-full flex items-center gap-3 p-4 text-left"
                  >
                    <div className={`w-10 h-10 rounded-xl ${colors.bg} flex items-center justify-center ${colors.text} flex-shrink-0`}>
                      {stage.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold ${colors.text}`}>ЭТАП {stage.num}</span>
                      </div>
                      <p className="text-white font-bold text-base sm:text-lg truncate">{stage.title}</p>
                    </div>
                    <ChevronDown 
                      className={`w-5 h-5 text-zinc-400 flex-shrink-0 transition-transform duration-300 ${
                        isExpanded ? 'rotate-180' : ''
                      }`} 
                    />
                  </button>
                  
                  {/* Stage Content - Expandable */}
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${
                      isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-4 pb-4 pt-0">
                      <div className="space-y-2 ml-[52px]">
                        {stage.items.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <Check className={`w-4 h-4 ${colors.text} flex-shrink-0 mt-0.5`} />
                            <span className="text-zinc-300 text-sm">{item}</span>
                          </div>
                        ))}
                        {stage.highlight && (
                          <div className={`mt-3 p-3 rounded-lg ${colors.bg} border ${colors.border}`}>
                            <p className={`${colors.text} text-sm font-semibold flex items-start gap-2`}>
                              <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              {stage.highlight}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Final Result Block */}
            <div className="mt-6 p-5 rounded-xl bg-gradient-to-br from-emerald-500/10 via-zinc-900 to-teal-500/10 border-2 border-emerald-500/40">
              <div className="flex items-center gap-2 mb-4">
                <Target className="w-5 h-5 text-emerald-400" />
                <h3 className="text-lg font-black text-white">Итог PRO</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-zinc-300 text-sm">Мы сделали для вас весь цикл</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-zinc-300 text-sm">Вы сделали весь цикл самостоятельно</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-400" />
                  <span className="text-zinc-300 text-sm">У вас есть продажи и понимание, как масштабироваться</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-amber-400" />
                  <span className="text-white text-sm font-semibold">После обучения — 6 месяцев личной поддержки</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={handleClose}
              className="w-full py-4 px-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-black font-black text-lg rounded-xl hover:from-emerald-400 hover:to-teal-400 transition-all duration-300 flex items-center justify-center gap-2 mt-4"
            >
              <span>Понятно!</span>
              <Check className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const TierCard = ({ tierId, discountMode = false, onShowFlowModal, onShowIncludesModal, onShowRoadmap, onCheckout, serverPrices, serverSettings }) => {
  const tier = tierConfig[tierId];
  const colors = colorStyles[tier.color];
  const { shouldApplyDiscount } = usePromo();
  
  // Use server settings or defaults
  const adminSettings = serverSettings || {
    flow_date: '',
    flow_time: '19:00',
  };
  
  // Get price from server or use default
  const serverPrice = serverPrices?.[packageIdMap[tierId]];
  const basePrice = serverPrice?.price || tier.price;
  
  const originalPrice = basePrice;
  let discountedPrice = originalPrice;
  let discountLabel = '';
  let hasDiscount = false;
  
  // === NEW per-tier discount logic ===
  // Each tier has its own enabled/type/value (e.g. base_discount_enabled, advanced_discount_enabled, etc.)
  // Rules:
  //  • flow: discount applies on ALL pages if flow_discount_enabled
  //  • base/advanced/pro: discount applies ONLY if user is on a discount page with valid promo
  //    (discountMode === true AND PromoContext says shouldApplyDiscount)
  const tierEnabled = adminSettings[`${tierId}_discount_enabled`];
  const tierType = adminSettings[`${tierId}_discount_type`] || 'percent';
  const tierValue = Number(adminSettings[`${tierId}_discount_value`] || 0);
  
  const applies = tier.available && tierEnabled && tierValue > 0 && (
    tierId === 'flow' ? true : (discountMode && shouldApplyDiscount)
  );
  
  if (applies) {
    hasDiscount = true;
    if (tierType === 'percent') {
      discountedPrice = Math.round(originalPrice * (1 - tierValue / 100));
      discountLabel = `-${tierValue}%`;
    } else {
      discountedPrice = Math.max(0, originalPrice - tierValue);
      discountLabel = `-$${tierValue}`;
    }
  }
  
  const finalPrice = hasDiscount ? discountedPrice : originalPrice;
  
  // Get flow date for "Поток" tier - показываем ВСЕГДА, не только в discountMode
  const flowDateStr = adminSettings.flow_date && adminSettings.flow_date.trim() !== '' ? adminSettings.flow_date : null;
  const flowDate = flowDateStr ? new Date(flowDateStr + 'T' + (adminSettings.flow_time || '19:00')) : null;
  const isValidFlowDate = flowDate && !isNaN(flowDate.getTime());
  const formattedFlowDate = isValidFlowDate 
    ? flowDate.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
    : null;

  const handleCtaClick = () => {
    if (tier.available && onCheckout) {
      onCheckout(packageIdMap[tierId], tier.name, finalPrice);
    }
  };

  return (
    <div className={`relative rounded-3xl bg-gradient-to-b ${colors.bg} backdrop-blur-xl border ${colors.border} p-5 sm:p-6 flex flex-col h-full hover-lift ${tier.premium ? 'ring-2 ring-emerald-500/50' : ''} ${!tier.available ? 'opacity-70' : ''}`}>
      
      {/* Discount Badge — animated entrance + soft pulse */}
      {hasDiscount && tier.available && (
        <div className="absolute -top-3 -right-3 z-10 discount-badge-pop">
          <div className="relative px-3 py-1.5 rounded-full bg-gradient-to-r from-red-500 to-rose-500 text-white text-xs font-black shadow-lg shadow-red-500/40 ring-2 ring-red-500/30">
            <span className="absolute inset-0 rounded-full bg-red-500/40 animate-ping-slow" aria-hidden />
            <span className="relative">{discountLabel}</span>
          </div>
        </div>
      )}
      
      {/* Flow Date Badge - показываем ВСЕГДА если это Поток и есть дата */}
      {tierId === 'flow' && formattedFlowDate && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
          <div className="px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-bold shadow-lg shadow-blue-500/30 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Старт: {formattedFlowDate}
          </div>
        </div>
      )}
      
      {/* Badges - только если НЕ flow с датой */}
      {tier.popular && tierId !== 'flow' && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="px-4 py-1.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 text-white text-xs font-bold shadow-lg shadow-violet-500/30 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Популярный
          </div>
        </div>
      )}
      
      {tier.premium && tierId !== 'flow' && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="px-4 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-xs font-bold shadow-lg shadow-emerald-500/30 flex items-center gap-1">
            <Crown className="w-3 h-3" /> VIP
          </div>
        </div>
      )}
      
      {!tier.available && tier.badge && tierId !== 'flow' && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div className="px-4 py-1.5 rounded-full bg-blue-600 text-white text-xs font-bold shadow-lg flex items-center gap-1">
            <Lock className="w-3 h-3" /> {tier.badge}
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="text-center mb-4 pt-2">
        <h3 className="text-lg sm:text-xl font-black text-white mb-1 heading-premium">{tier.name}</h3>
        <p className="text-xs text-zinc-500">{tier.subtitle}</p>
        {tier.resultLine && (
          <p className={`text-xs ${colors.text} font-semibold mt-2`}>{tier.resultLine}</p>
        )}
      </div>
      
      {/* Price */}
      <div className="text-center mb-5">
        {hasDiscount && tier.available ? (
          <>
            <div className="mb-1">
              <span className="text-2xl text-zinc-500 line-through font-bold">${originalPrice.toLocaleString()}</span>
            </div>
            <span className={`text-4xl sm:text-5xl font-black ${colors.text}`}>${discountedPrice.toLocaleString()}</span>
          </>
        ) : (
          <span className={`text-4xl sm:text-5xl font-black ${colors.text}`}>${finalPrice.toLocaleString()}</span>
        )}
        <p className="text-zinc-600 text-xs mt-1">единоразово</p>
      </div>
      
      {/* Features - Detailed list */}
      <div className="flex-1 space-y-2.5 mb-6">
        {tier.features.map((feature, i) => (
          <FeatureRow key={i} feature={feature} colors={colors} />
        ))}
      </div>
      
      {/* CTA Buttons */}
      {tierId === 'flow' && tier.available ? (
        // Special dual-button layout for Flow tier
        <div className="space-y-2">
          {/* Full Payment Button */}
          <button 
            onClick={() => onCheckout && onCheckout('flow', 'Поток — Полный доступ', finalPrice)}
            className={`cta-premium w-full py-3.5 rounded-xl font-bold text-white transition-all duration-300 flex items-center justify-center gap-2 text-sm ${colors.button} hover:-translate-y-0.5 hover:shadow-lg`}
          >
            <Play className="w-4 h-4" />
            <span>Полная оплата — ${finalPrice}</span>
          </button>
          
          {/* Booking Button */}
          <button 
            onClick={() => onCheckout && onCheckout('booking', 'Бронь места в Поток', serverPrices?.booking?.price || 80)}
            className="w-full py-3 rounded-xl font-semibold text-blue-400 border-2 border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10 hover:border-blue-500/50 transition-all duration-300 flex items-center justify-center gap-2 text-sm"
          >
            <Clock className="w-4 h-4" />
            <span>Забронировать место — ${serverPrices?.booking?.price || 80}</span>
          </button>
          
          {/* Info text */}
          <p className="text-zinc-500 text-xs text-center pt-1">
            Бронь = гарантия места. Полная = доступ сразу.
          </p>
        </div>
      ) : (
        // Standard single button for other tiers
        <button 
          onClick={handleCtaClick}
          className={`cta-premium w-full py-3.5 rounded-xl font-bold text-white transition-all duration-300 flex items-center justify-center gap-2 text-sm ${
            tier.available ? `${colors.button} hover:-translate-y-0.5 hover:shadow-lg` : 'bg-zinc-800 cursor-not-allowed'
          }`}
          disabled={!tier.available}
        >
          {tier.available ? (
            <>
              <CreditCard className="w-4 h-4" />
              <span>{tier.cta}</span>
              <ArrowRight className="w-4 h-4" />
            </>
          ) : 'Скоро'}
        </button>
      )}
      
      {/* "Что будет в потоке?" button - только для Поток */}
      {tierId === 'flow' && tier.available && (
        <button
          onClick={onShowFlowModal}
          className="mt-3 w-full py-3 px-4 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border-2 border-blue-500/50 text-blue-300 font-bold text-sm hover:from-blue-500/30 hover:to-indigo-500/30 hover:border-blue-400 transition-all duration-300 flex items-center justify-center gap-2 group"
        >
          <Info className="w-4 h-4" />
          <span>👀 Смотреть программу потока</span>
          <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
        </button>
      )}
      
      {/* "Roadmap Поток" button - только для тарифа Поток */}
      {tierId === 'flow' && tier.available && (
        <button
          onClick={() => onShowRoadmap && onShowRoadmap('flow')}
          className="mt-2 w-full py-2.5 px-4 rounded-xl border font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border-blue-500/40 text-blue-300 hover:from-blue-500/20 hover:to-indigo-500/20 hover:border-blue-500/60"
        >
          <Map className="w-4 h-4" />
          <span>Roadmap: 9 недель до продаж</span>
        </button>
      )}
      
      {/* "Что входит?" button - для всех кроме Поток */}
      {tierId !== 'flow' && tier.available && (
        <button
          onClick={() => onShowIncludesModal && onShowIncludesModal(tierId)}
          className={`mt-3 w-full py-3 px-4 rounded-xl border-2 font-bold text-sm transition-all duration-300 flex items-center justify-center gap-2 group ${
            tierId === 'base' 
              ? 'bg-gradient-to-r from-amber-500/15 to-orange-500/15 border-amber-500/50 text-amber-300 hover:from-amber-500/25 hover:to-orange-500/25 hover:border-amber-400' 
              : tierId === 'advanced'
              ? 'bg-gradient-to-r from-violet-500/15 to-purple-500/15 border-violet-500/50 text-violet-300 hover:from-violet-500/25 hover:to-purple-500/25 hover:border-violet-400'
              : 'bg-gradient-to-r from-emerald-500/15 to-teal-500/15 border-emerald-500/50 text-emerald-300 hover:from-emerald-500/25 hover:to-teal-500/25 hover:border-emerald-400'
          }`}
        >
          <Info className="w-4 h-4" />
          <span>👀 Что внутри тарифа?</span>
          <ChevronDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
        </button>
      )}
      
      {/* "Roadmap PRO" button - только для PRO тарифа */}
      {tierId === 'pro' && tier.available && (
        <button
          onClick={() => onShowRoadmap && onShowRoadmap('pro')}
          className="mt-2 w-full py-2.5 px-4 rounded-xl border font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/40 text-emerald-300 hover:from-emerald-500/20 hover:to-teal-500/20 hover:border-emerald-500/60"
        >
          <Map className="w-4 h-4" />
          <span>Roadmap: как запустим ваш бизнес</span>
        </button>
      )}
    </div>
  );
};

// Discount Banner — shown when user has a valid promo (signed link or 24h visitor session)
const DiscountBanner = ({ serverSettings }) => {
  const { hasValidPromo, isExpired, loaded } = usePromo();
  const adminSettings = serverSettings || {};

  if (!loaded) return null;
  if (!hasValidPromo) return null;

  // Find the biggest discount among enabled tiers (base/advanced/pro — flow has its own)
  const tiers = ['base', 'advanced', 'pro'];
  let bestLabel = '';
  let bestPct = 0;
  tiers.forEach((t) => {
    if (!adminSettings[`${t}_discount_enabled`]) return;
    const v = Number(adminSettings[`${t}_discount_value`] || 0);
    if (v <= 0) return;
    const type = adminSettings[`${t}_discount_type`] || 'percent';
    const label = type === 'percent' ? `${v}%` : `$${v}`;
    const pctEquivalent = type === 'percent' ? v : v; // crude comparison
    if (pctEquivalent > bestPct) {
      bestPct = pctEquivalent;
      bestLabel = label;
    }
  });

  // Fallback when no per-tier discounts are enabled but the user has a promo
  if (!bestLabel) bestLabel = 'Скидки';
  
  return (
    <div className="mb-8 sm:mb-12 discount-banner-enter">
      <div className="relative">
        {/* Glow — gentle, slower pulse for mobile readability */}
        <div className="absolute -inset-1.5 sm:-inset-2 bg-gradient-to-r from-red-500/30 via-amber-500/40 to-red-500/30 rounded-3xl blur-2xl animate-glow-soft" />
        
        {/* Banner */}
        <div className="relative bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 rounded-2xl p-5 sm:p-8 border border-amber-500/30 text-center" data-testid="discount-banner">
          <div className="flex items-center justify-center gap-2 mb-3 sm:mb-4">
            <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
            <span className="text-lg sm:text-2xl font-black text-white">Специальное предложение</span>
          </div>
          
          {isExpired ? (
            <p className="text-zinc-400 text-sm sm:text-lg mb-2">
              Скидка <span className="text-red-400 font-black">закончилась</span>. Цены восстановлены.
            </p>
          ) : (
            <p className="text-zinc-400 text-sm sm:text-lg mb-5 sm:mb-6 px-2">
              Скидка <span className="text-amber-400 font-black text-lg sm:text-xl">до {bestLabel}</span> на тарифы. Осталось времени:
            </p>
          )}
          
          <CountdownTimer />
        </div>
      </div>
    </div>
  );
};

// Installment notice — shown on /discount and /faster_discount under the timer banner.
// Click to expand → shows 2-month breakdown for each tier (current prices, discounts applied).
const InstallmentNotice = ({ serverPrices, serverSettings }) => {
  const [expanded, setExpanded] = useState(false);
  const { shouldApplyDiscount } = usePromo();

  // Compute current price for each tier with the same discount logic as TierCard.
  // Returns: [{ id, name, color, finalPrice, monthlyPrice }]
  const installmentRows = useMemo(() => {
    const tiers = ['base', 'flow', 'advanced', 'pro'];
    return tiers.map((tierId) => {
      const tier = tierConfig[tierId];
      const serverPrice = serverPrices?.[packageIdMap[tierId]];
      const basePrice = serverPrice?.price || tier.price;

      const enabled = serverSettings?.[`${tierId}_discount_enabled`];
      const type = serverSettings?.[`${tierId}_discount_type`] || 'percent';
      const value = Number(serverSettings?.[`${tierId}_discount_value`] || 0);
      const applies = enabled && value > 0 && (tierId === 'flow' ? true : shouldApplyDiscount);

      let finalPrice = basePrice;
      if (applies) {
        finalPrice = type === 'percent'
          ? Math.round(basePrice * (1 - value / 100))
          : Math.max(0, basePrice - value);
      }
      const monthlyPrice = Math.ceil(finalPrice / 2);
      return {
        id: tierId,
        name: tier.name,
        color: tier.color,
        finalPrice,
        monthlyPrice,
        hasDiscount: applies,
      };
    });
  }, [serverPrices, serverSettings, shouldApplyDiscount]);

  return (
    <div className="mb-12 sm:mb-14 discount-banner-enter" style={{ animationDelay: '120ms' }}>
      <div className="relative max-w-4xl mx-auto">
        {/* Soft cyan glow */}
        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-cyan-500/20 rounded-3xl blur-xl" aria-hidden />

        <div
          className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-cyan-500/10 via-zinc-900 to-blue-500/10 border border-cyan-500/30 shadow-xl shadow-cyan-500/10"
          data-testid="installment-notice"
        >
          {/* Top stripe */}
          <div className="h-1 w-full bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400" />

          {/* Clickable header row */}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full p-5 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 text-left hover:bg-cyan-500/5 transition-colors"
            data-testid="installment-toggle"
            aria-expanded={expanded}
          >
            {/* Icon */}
            <div className="flex-shrink-0 relative">
              <div className="absolute -inset-1 bg-cyan-400/40 rounded-2xl blur" aria-hidden />
              <div className="relative w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <Calendar className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-[10px] sm:text-xs font-black uppercase tracking-wider">
                  Удобная оплата
                </span>
                <span className="text-zinc-500 text-xs sm:text-sm">— нет суммы целиком? не проблема</span>
              </div>
              <h3 className="text-white text-lg sm:text-2xl font-black leading-tight mb-1.5">
                Любой тариф можно разбить{' '}
                <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
                  на 2 месяца
                </span>
              </h3>
              <p className="text-zinc-300 text-sm sm:text-base leading-relaxed">
                Платите <span className="text-white font-bold">равными частями</span> — без переплат и процентов.{' '}
                <span className="text-cyan-300 font-semibold underline-offset-2 underline decoration-cyan-500/40">
                  {expanded ? 'Свернуть' : 'Посмотреть суммы по месяцам'}
                </span>
              </p>
            </div>

            {/* Mini visual: 2 month slots + chevron */}
            <div className="flex flex-shrink-0 items-center gap-2 px-1">
              <div className="hidden sm:flex items-center gap-1.5">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-400/40 flex items-center justify-center text-cyan-200 text-sm font-black">
                  1
                </div>
                <ArrowRight className="w-3 h-3 text-cyan-500/60" />
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-400/40 flex items-center justify-center text-cyan-200 text-sm font-black">
                  2
                </div>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-cyan-300 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
              />
            </div>
          </button>

          {/* Expanded breakdown */}
          {expanded && (
            <div className="border-t border-cyan-500/20 px-5 sm:px-7 py-5 sm:py-6 bg-zinc-900/40">
              <p className="text-zinc-500 text-[11px] sm:text-xs uppercase tracking-wider mb-3 font-bold">
                Разбивка по месяцам {shouldApplyDiscount ? '— с учётом текущей скидки' : ''}
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                {installmentRows.map((r) => {
                  const c = colorStyles[r.color] || colorStyles.zinc;
                  return (
                    <div
                      key={r.id}
                      className={`p-3.5 rounded-xl bg-zinc-800/60 border ${c.border} flex items-center justify-between gap-3`}
                      data-testid={`installment-row-${r.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-white font-bold text-sm sm:text-base truncate">{r.name}</p>
                        <p className="text-zinc-500 text-xs">
                          {r.hasDiscount && <span className="text-cyan-300">со скидкой </span>}
                          итого ${r.finalPrice.toLocaleString('en-US')}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`${c.text} font-black text-base sm:text-lg tabular-nums leading-none`}>
                          ${r.monthlyPrice.toLocaleString('en-US')}
                        </p>
                        <p className="text-zinc-500 text-[10px] sm:text-xs mt-0.5">× 2 мес</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Contact line */}
              <a
                href="https://t.me/vadjik"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 flex items-center gap-3 p-3.5 rounded-xl bg-gradient-to-r from-cyan-500/15 to-blue-500/15 border border-cyan-500/40 hover:from-cyan-500/25 hover:to-blue-500/25 transition-colors group"
                data-testid="installment-contact-link"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm sm:text-base">
                    Для оплаты по-месячно — напишите{' '}
                    <span className="text-cyan-300 font-bold">@vadjik в Telegram</span>
                  </p>
                  <p className="text-zinc-500 text-xs mt-0.5">подберём удобный график оплаты</p>
                </div>
                <ArrowRight className="w-4 h-4 text-cyan-300 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PricingSection = ({ discountMode = false }) => {
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [showIncludesModal, setShowIncludesModal] = useState(null); // 'base', 'advanced', 'pro'
  const [showRoadmap, setShowRoadmap] = useState(null); // 'pro', 'flow', or null
  const [checkoutData, setCheckoutData] = useState({ isOpen: false, packageId: '', packageName: '', price: 0 });
  const [serverPrices, setServerPrices] = useState(null);
  const [serverSettings, setServerSettings] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  
  // Fetch prices and settings from server
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pricesRes, settingsRes] = await Promise.all([
          fetch(`${API_URL}/api/packages`),
          fetch(`${API_URL}/api/admin/settings`)
        ]);
        
        if (pricesRes.ok) {
          const pricesData = await pricesRes.json();
          setServerPrices(pricesData);
        }
        
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setServerSettings(settingsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);
  
  const handleCheckout = (packageId, packageName, price) => {
    setCheckoutData({ isOpen: true, packageId, packageName, price });
  };
  
  const closeCheckout = () => {
    setCheckoutData({ isOpen: false, packageId: '', packageName: '', price: 0 });
  };
  
  return (
    <>
      {/* Modals - rendered outside section to avoid overflow:hidden clipping */}
      <FlowModal isOpen={showFlowModal} onClose={() => setShowFlowModal(false)} />
      <BaseIncludesModal isOpen={showIncludesModal === 'base'} onClose={() => setShowIncludesModal(null)} />
      <AdvancedIncludesModal isOpen={showIncludesModal === 'advanced'} onClose={() => setShowIncludesModal(null)} />
      <ProIncludesModal isOpen={showIncludesModal === 'pro'} onClose={() => setShowIncludesModal(null)} />
      <ProRoadmapModal isOpen={showRoadmap === 'pro'} onClose={() => setShowRoadmap(null)} />
      <FlowRoadmapModal isOpen={showRoadmap === 'flow'} onClose={() => setShowRoadmap(null)} />
      <CheckoutModal 
        isOpen={checkoutData.isOpen}
        onClose={closeCheckout}
        packageId={checkoutData.packageId}
        packageName={checkoutData.packageName}
        price={checkoutData.price}
        discountMode={discountMode}
      />
      
      <section ref={sectionRef} className="relative py-20 sm:py-24 lg:py-32 overflow-hidden bg-[#050506]" id="pricing">
      
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-[10%] left-[10%] w-[600px] h-[600px] bg-emerald-500/[0.04] rounded-full blur-[200px] animate-pulse" />
        <div className="absolute bottom-[10%] right-[10%] w-[500px] h-[500px] bg-violet-500/[0.04] rounded-full blur-[150px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-500/[0.02] rounded-full blur-[250px]" />
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 rounded-full animate-pulse"
            style={{
              background: i % 3 === 0 ? '#f59e0b' : i % 3 === 1 ? '#8b5cf6' : '#10b981',
              left: `${5 + (i * 5)}%`,
              top: `${10 + (i * 4) % 80}%`,
              animationDelay: `${i * 0.15}s`,
              animationDuration: `${2 + i * 0.2}s`
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Discount Timer Banner */}
        {discountMode && <DiscountBanner serverSettings={serverSettings} />}
        {discountMode && <InstallmentNotice serverPrices={serverPrices} serverSettings={serverSettings} />}
        
        {/* MEGA Header */}
        <div className={`text-center mb-12 sm:mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {/* Badge with glow */}
          <div className="relative inline-block mb-6">
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/50 via-emerald-500/50 to-violet-500/50 rounded-full blur-lg animate-pulse" />
            <div className="relative inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-amber-500/20 via-emerald-500/20 to-violet-500/20 border border-amber-500/40 backdrop-blur-xl">
              <Sparkles className="w-4 h-4 text-amber-400 animate-spin" style={{ animationDuration: '3s' }} />
              <span className="text-amber-300 font-bold text-sm tracking-wide">ТАРИФЫ</span>
            </div>
          </div>
          
          {/* Animated title */}
          <h2 className={`text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-white">Выбери свой </span>
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                путь
              </span>
              {/* Decorative elements */}
              <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 100 12" fill="none" preserveAspectRatio="none">
                <path d="M2 10C25 2 75 2 98 10" stroke="url(#pricing-underline)" strokeWidth="4" strokeLinecap="round" strokeDasharray="100" strokeDashoffset="100" style={{ animation: isVisible ? 'draw 1.5s ease-out forwards 0.5s' : 'none' }}/>
                <defs>
                  <linearGradient id="pricing-underline" x1="0" y1="0" x2="100" y2="0">
                    <stop stopColor="#f59e0b"/>
                    <stop offset="0.5" stopColor="#f97316"/>
                    <stop offset="1" stopColor="#f59e0b"/>
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>
          
          {/* Subtitle with animation */}
          <p className={`text-lg sm:text-xl text-zinc-400 max-w-xl mx-auto transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            От самостоятельного обучения до <span className="text-white font-semibold">полного сопровождения</span>
          </p>
        </div>

        {/* Pricing Grid - 4 cards with staggered animation */}
        <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-12 transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <TierCard tierId="base" discountMode={discountMode} onShowFlowModal={() => setShowFlowModal(true)} onShowIncludesModal={setShowIncludesModal} onShowRoadmap={setShowRoadmap} onCheckout={handleCheckout} serverPrices={serverPrices} serverSettings={serverSettings} />
          <TierCard tierId="flow" discountMode={discountMode} onShowFlowModal={() => setShowFlowModal(true)} onShowIncludesModal={setShowIncludesModal} onShowRoadmap={setShowRoadmap} onCheckout={handleCheckout} serverPrices={serverPrices} serverSettings={serverSettings} />
          <TierCard tierId="advanced" discountMode={discountMode} onShowFlowModal={() => setShowFlowModal(true)} onShowIncludesModal={setShowIncludesModal} onShowRoadmap={setShowRoadmap} onCheckout={handleCheckout} serverPrices={serverPrices} serverSettings={serverSettings} />
          <TierCard tierId="pro" discountMode={discountMode} onShowFlowModal={() => setShowFlowModal(true)} onShowIncludesModal={setShowIncludesModal} onShowRoadmap={setShowRoadmap} onCheckout={handleCheckout} serverPrices={serverPrices} serverSettings={serverSettings} />
        </div>
      </div>
      
      {/* CSS for draw animation */}
      <style>{`
        @keyframes draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </section>
    </>
  );
};

export default PricingSection;
